/**
 * Ballistic missile flight model, shared by the server sim and client extrapolation
 * (docs/GAME_DESIGN.md §1.7). Pure arithmetic only (no trig) so it is bit-identical everywhere.
 */
import { BALANCE, TICK_HZ } from './balance.js';
import type { MissileKind, Vec2 } from './types.js';

/** Fields of a missile / MissileTrack needed to place it in time. */
export interface MissilePathParams {
  kind: MissileKind;
  ox: number;
  oy: number;
  tx: number;
  ty: number;
  launchTick: number;
  flightTicks: number;
}

/** Flight duration in seconds for a ground distance d (mu). */
export function missileFlightSeconds(kind: MissileKind, d: number): number {
  if (kind === 'icbm') {
    const m = BALANCE.missiles.icbm;
    return Math.min(m.maxS, Math.max(m.minS, m.base + m.perMu * d));
  }
  const m = BALANCE.missiles.slbm;
  return m.base + m.perMu * d;
}

/** Flight duration in whole ticks. */
export function missileFlightTicks(kind: MissileKind, d: number): number {
  return Math.round(missileFlightSeconds(kind, d) * TICK_HZ);
}

/** Bezier control point: the midpoint pushed poleward (−y) by bend·d. */
export function missileControlPoint(kind: MissileKind, ox: number, oy: number, tx: number, ty: number): Vec2 {
  const d = Math.sqrt((tx - ox) ** 2 + (ty - oy) ** 2);
  const bend = kind === 'icbm' ? BALANCE.missiles.icbm.bend : BALANCE.missiles.slbm.bend;
  return { x: (ox + tx) / 2, y: (oy + ty) / 2 - bend * d };
}

/** Bezier parameter u for normalised flight time τ ∈ [0, 1]. */
export function missileProgress(kind: MissileKind, tau: number): number {
  const t = tau < 0 ? 0 : tau > 1 ? 1 : tau;
  if (kind === 'slbm') return t;
  const { boostTau, boostU } = BALANCE.missiles.icbm;
  return t <= boostTau ? (boostU * t) / boostTau : boostU + ((1 - boostU) * (t - boostTau)) / (1 - boostTau);
}

/** Inverse of `missileProgress`: τ at which the missile reaches Bezier parameter u. */
export function missileTauAt(kind: MissileKind, u: number): number {
  const v = u < 0 ? 0 : u > 1 ? 1 : u;
  if (kind === 'slbm') return v;
  const { boostTau, boostU } = BALANCE.missiles.icbm;
  return v <= boostU ? (v * boostTau) / boostU : boostTau + ((v - boostU) * (1 - boostTau)) / (1 - boostU);
}

/** Point on the quadratic Bezier at parameter u. */
export function bezierPoint(ox: number, oy: number, cx: number, cy: number, tx: number, ty: number, u: number, out: Vec2 = { x: 0, y: 0 }): Vec2 {
  const a = (1 - u) * (1 - u);
  const b = 2 * u * (1 - u);
  const c = u * u;
  out.x = a * ox + b * cx + c * tx;
  out.y = a * oy + b * cy + c * ty;
  return out;
}

/** Missile position at (possibly fractional) tick. Clamped to origin / target outside flight. */
export function missilePos(m: MissilePathParams, tick: number, out: Vec2 = { x: 0, y: 0 }): Vec2 {
  const tau = (tick - m.launchTick) / m.flightTicks;
  const u = missileProgress(m.kind, tau);
  const c = missileControlPoint(m.kind, m.ox, m.oy, m.tx, m.ty);
  return bezierPoint(m.ox, m.oy, c.x, c.y, m.tx, m.ty, u, out);
}

/** Impact tick of a missile. */
export function missileImpactTick(m: Pick<MissilePathParams, 'launchTick' | 'flightTicks'>): number {
  return m.launchTick + m.flightTicks;
}

/**
 * Seconds the missile spends within `radius` mu of its target before impact (the reaction
 * window of a sensor centred on the target). Bisection on the monotone tail of the arc.
 */
export function reactionWindowSeconds(kind: MissileKind, d: number, radius: number): number {
  if (radius >= d) return missileFlightSeconds(kind, d);
  const ox = 0;
  const oy = 0;
  const tx = d;
  const ty = 0;
  const c = missileControlPoint(kind, ox, oy, tx, ty);
  const p: Vec2 = { x: 0, y: 0 };
  const inside = (u: number) => {
    bezierPoint(ox, oy, c.x, c.y, tx, ty, u, p);
    return (p.x - tx) ** 2 + (p.y - ty) ** 2 <= radius * radius;
  };
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (inside(mid)) hi = mid;
    else lo = mid;
  }
  return (1 - missileTauAt(kind, hi)) * missileFlightSeconds(kind, d);
}
